import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, CreditCard } from "lucide-react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { authHeaders, Axios } from "@/utils/axios";
import { API_END_POINTS } from "@/utils/routes";
import { useUserStore } from "@/store/userStore";

const PaymentHistory = () => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const { user } = useUserStore();

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await Axios.get(
          `${API_END_POINTS.TRANSACTION_HISTORY}`,
          authHeaders(user?.accessToken)
        );

        // Filter contracts with at least one paid milestone or totalPaid > 0
        const paidTransactions = (res.data.data.contracts ?? []).filter(
          (c: any) =>
            c.paymentSummary?.totalPaid > 0 ||
            (c.milestones?.some((m: any) => m.paid))
        );

        setTransactions(paidTransactions);
      } catch (error) {
        console.error("Failed to fetch payment history:", error);
      }
    };

    if (user?.accessToken) fetchHistory();
  }, [user?.accessToken]);

  return (
    <Card className="hover:shadow-md transition-all duration-300 border-green-100 bg-gradient-to-br from-white to-green-50/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-xl flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 flex items-center justify-center shadow-inner">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            Paid History
          </span>
        </CardTitle>
        <p className="text-gray-500 mt-1">View only paid contracts and milestones.</p>
      </CardHeader>

      <CardContent>
        {transactions.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-gradient-to-br from-green-50/50 to-emerald-50/50 rounded-lg border border-dashed border-green-200 p-6">
            <p className="text-emerald-500 font-medium">No paid history available.</p>
          </div>
        ) : (
          <div className="relative pl-4 border-l-2 border-emerald-100 space-y-8">
            {transactions.map((transaction) => {
              const paidMilestones = transaction?.milestones?.filter((m: any) => m.paid) ?? [];
              const latestPaidAt = transaction?.contract?.completionPaymentReleasedAt || 
                (paidMilestones.length
                  ? paidMilestones
                      .slice()
                      .sort((a: any, b: any) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime())[0]
                      ?.paidAt
                  : null);

              return (
                <div key={transaction?.contract?.id} className="relative">
                  {/* Contract Dot */}
                  <div className="absolute left-[-30px] w-6 h-6 rounded-full bg-emerald-50 border flex items-center justify-center shadow-sm z-10">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>

                  {/* Contract Card */}
                  <div className="ml-4 border border-green-100 rounded-lg p-4 bg-green-50 shadow-sm hover:shadow transition-all">
                    <div className="flex justify-between items-center mb-2">
                      <p className="font-semibold text-gray-800">{transaction?.job?.title}</p>
                      <Badge className="text-xs px-3 py-1 rounded-full bg-green-100 text-green-800">Paid</Badge>
                    </div>

                    <div className="flex justify-between text-sm text-gray-600 mb-3">
                      <span>Total Paid: {transaction?.paymentSummary?.totalPaid ?? 0} USDC</span>
                      <span>{latestPaidAt ? format(new Date(latestPaidAt), "MMM dd, yyyy") : "—"}</span>
                    </div>

                    {/* Paid Milestones */}
                    {paidMilestones.length > 0 && (
                      <div>
                        <p className="text-gray-700 font-medium text-sm mb-2">Paid Milestones</p>
                        <div className="relative space-y-2 pl-6 border-l border-emerald-100">
                          {paidMilestones.map((milestone: any) => (
                            <div key={milestone.milestoneId} className="relative">
                              {/* Milestone Dot */}
                              <div className="absolute left-[-34px] top-[10px] w-5 h-5 rounded-full bg-emerald-50 border flex items-center justify-center shadow-sm z-10">
                                <CheckCircle className="w-3 h-3 text-green-500" />
                              </div>

                              <div className="ml-2 flex justify-between items-center text-sm text-gray-600 p-2 rounded border border-green-100 bg-white">
                                <span className="flex-1">{milestone.title}</span>
                                <span className="w-20 text-right">{milestone.amount} USDC</span>
                                <Badge className="ml-4 text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">Released</Badge>
                                <span className="w-28 text-right">
                                  {milestone.paidAt ? format(new Date(milestone.paidAt), "MMM dd, yyyy") : "—"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentHistory;